import type { UWMCLibraryCommonDomainEnumsAusUnderwritingMethod } from '~/__generated__/api';

import type {
  LPALoanAssociationDetails,
  LoanAssociationDetails,
} from './AUSType';

export const getAgencyOptions = (
  selectedAusOption: string,
  useBrokerCredentials: boolean,
  lpaCreditCompanies: any[],
  brokerAssociatedCreditCompanies: any[],
  duCreditCompanies: any[],
  includeDefault: boolean = true,
) => {
  let options: Array<{
    value: string;
    label: string;
    key: string | number;
    disabled?: boolean;
  }>;

  if (selectedAusOption === 'LPA') {
    options = lpaCreditCompanies.map((item: any) => ({
      value: String(item.creditCompanyRecordId),
      label: item.companyName,
      key: item.creditCompanyRecordId,
    }));
  } else if (useBrokerCredentials) {
    options = brokerAssociatedCreditCompanies.map((item: any, idx: number) => ({
      value: item.creditCompany,
      label: item.description ?? item.name,
      key: item.companyId ?? idx,
    }));
  } else {
    options = duCreditCompanies.map((item: any) => ({
      value: item.creditCompany,
      label: item.description,
      key: item.companyId,
    }));
  }

  if (includeDefault) {
    options.unshift({
      value: '',
      label: 'Select one',
      key: 'default',
      disabled: true,
    });
  }

  return options;
};

export function getInitialAgency(
  selectedAusOption: string,
  lpaLoanAssociationDetails: LPALoanAssociationDetails,
  loanAssociationDetails: LoanAssociationDetails,
): string {
  if (selectedAusOption === 'LPA') {
    const id = lpaLoanAssociationDetails?.creditCompanyRecordId;
    return id == null ? '' : String(id);
  }
  if (selectedAusOption === 'DU') {
    return loanAssociationDetails?.creditCompany ?? '';
  }
  return '';
}

function getCompanyId(
  selectedAusOption: string,
  formData: any,
  creditCompanies: any[],
  loanAssociationDetails: LoanAssociationDetails,
) {
  if (selectedAusOption === 'DU') {
    const selectedCompany = creditCompanies?.find(
      (company: any) => company.creditCompany === formData.agency,
    );
    return {
      companyId: selectedCompany?.companyId ?? null,
      creditCompany: selectedCompany?.creditCompany ?? '',
    };
  }
  const rawCompanyId = loanAssociationDetails.companyId;
  return {
    companyId:
      typeof rawCompanyId === 'string'
        ? Number.parseInt(rawCompanyId, 10)
        : (rawCompanyId ?? null),
    creditCompany: '',
  };
}

function getOriginalCreditEntityId(
  selectedAusOption: string,
  formData: any,
  lpaLoanAssociationDetails: LPALoanAssociationDetails,
) {
  if (selectedAusOption !== 'LPA') return undefined;
  if (formData.agency != null && formData.agency !== '') {
    return Number(formData.agency);
  }
  if (lpaLoanAssociationDetails.originalCreditEntityId != null) {
    return lpaLoanAssociationDetails.originalCreditEntityId;
  }
  return 0;
}

export function buildAUSPayload({
  formData,
  selectedAusOption,
  creditOption,
  lpaLoanAssociationDetails,
  loanAssociationDetails,
  creditCompanies,
  borrowerCreditReferences,
  useBrokerCredentials,
  obfuscatedLenderDatabaseId,
  obfuscatedLoanRecordId,
}: any) {
  const { companyId, creditCompany } = getCompanyId(
    selectedAusOption,
    formData,
    creditCompanies,
    loanAssociationDetails,
  );
  const originalCreditEntityId = getOriginalCreditEntityId(
    selectedAusOption,
    formData,
    lpaLoanAssociationDetails,
  );

  return {
    obfuscatedLoanIdentifier: {
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    },
    reIssueIndicator: creditOption === 'reissue',
    ausMethod:
      selectedAusOption === 'DU'
        ? (1 as UWMCLibraryCommonDomainEnumsAusUnderwritingMethod)
        : (2 as UWMCLibraryCommonDomainEnumsAusUnderwritingMethod),
    desktopUnderwriterDetails: {
      creditCompany: selectedAusOption === 'DU' ? creditCompany : undefined,
      companyId: selectedAusOption === 'DU' ? String(companyId) : undefined,
      username: formData.userName ?? '',
      password: formData.password ?? '',
      brokerCredentialsInd: useBrokerCredentials,
    },
    loanProductAdvisorDetails: {
      originalCreditEntityId,
      creditCompanyRecordId:
        selectedAusOption === 'LPA' ? Number(formData.agency) : undefined,
      branchNumber:
        selectedAusOption === 'LPA' ? formData.lpaBranchNumber : undefined,
      freOfferingId:
        selectedAusOption === 'LPA' ? formData.freOfferingId : undefined,
    },
    customerCreditReferences: borrowerCreditReferences.map((borrower: any) => {
      const fieldName = `ref_${borrower.borrowerIdentifier?.customerRecordId}`;
      return {
        customerIdentifier: {
          borrowerLenderDatabaseId:
            borrower.borrowerIdentifier?.obfuscatedLenderDatabaseId ?? 1,
          borrowerRecordId: borrower.borrowerIdentifier?.customerRecordId ?? 0,
        },
        creditReference: formData[fieldName] ?? borrower.creditReference ?? '',
      };
    }),
  };
}

export function isAUSStatusSuccess(status?: string): boolean {
  if (!status) return false;
  const normalized = status.toLowerCase();
  return (
    ['approve/eligible', 'accept'].some((kw) => normalized.includes(kw)) &&
    !normalized.includes('ineligible')
  );
}

/**
 * Opens an AUS report (findings or credit) in a new tab.
 * Handles both HTML and URL responses.
 * Returns an error string if no report is available, otherwise undefined.
 */
export async function openAUSReport(
  refetch: () => Promise<{ data?: string }>,
  label: string,
): Promise<string | undefined> {
  const { data: url } = await refetch();
  if (!url) {
    return `No ${label} available for this request.`;
  }

  if (url.startsWith('data:text/html')) {
    const html = decodeURIComponent(url.split(',')[1]);
    const win = window.open('', '_blank');
    if (win) {
      win.document.title = label;
      win.document.body.innerHTML = html;
    }
  } else {
    window.open(url, '_blank');
  }
  return undefined;
}

export const getAgencyValue = (
  ausMethod: string,
  loanAssociationDetails: LoanAssociationDetails,
  lpaLoanAssociationDetails: LPALoanAssociationDetails,
) => {
  if (ausMethod === 'DU') {
    return loanAssociationDetails.creditCompany ?? '';
  }
  if (ausMethod === 'LPA') {
    return lpaLoanAssociationDetails.creditCompanyRecordId ?? '';
  }
};
